'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, RefreshCw, Users, Check, Clock, Trash2, Plus, SlidersHorizontal, ChevronLeft, ChevronRight, X, UserCheck, CalendarDays, Loader2, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API = 'https://silvamethod.in/manage/api/v1/goausers';
const EXCEL_COLUMNS = ['id', 'full_name', 'email', 'phone', 'is_active', 'checked_in', 'checked_in_at', 'day1', 'day2', 'day3', 'day4', 'day5', 'created_at'];
const date = (v) => typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }).format(new Date(v)) : '—';
const field = 'rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 focus:border-purple-600 focus:outline-purple-600';
export default function Home() {
    const [leads, setLeads] = useState([]),
        [loading, setLoading] = useState(true),
        [error, setError] = useState(''),
        [notice, setNotice] = useState(''),
        [query, setQuery] = useState(''),
        [status, setStatus] = useState('all'),
        [active, setActive] = useState('all'),
        [day, setDay] = useState('all'),
        [attendance, setAttendance] = useState('present'),
        [sort, setSort] = useState('newest'),
        [page, setPage] = useState(1),
        [size, setSize] = useState(10),
        [busy, setBusy] = useState(null),
        [confirm, setConfirm] = useState(null),
        [updated, setUpdated] = useState(null);

    const [showAddModal, setShowAddModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    async function load() {
        setLoading(true);
        setError('');
        try {
            const r = await fetch('https://silvamethod.in/manage/api/v1/goausers/get-goa-users');
            const j = await r.json();
            if (!r.ok || j.success === false) throw Error(j.message || 'Could not load leads');
            const rows = Array.isArray(j) ? j : j.data;
            if (!Array.isArray(rows)) throw Error('The API returned an unexpected leads format.');
            setLeads(rows);
            setUpdated(new Date());
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not load leads');
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => { void load() }, []);
    useEffect(() => {
        setPage(1)
    }, [query, status, active, day, attendance, sort, size]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return leads.filter(l => (!q ||
            [l.full_name, l.phone, l.email, l.id].some(v => String(v ?? '').toLowerCase().includes(q))) && (status === 'all' ||
                l.checked_in === (status === 'in')) && (active === 'all' ||
                    l.is_active === (active === 'active')) && (day === 'all' ||
                        Boolean(l[day]) === (attendance === 'present'))).sort((a, b) => sort === 'name' ? String(a.full_name ||
                            '').localeCompare(String(b.full_name ||
                                '')) : sort === 'id' ? a.id - b.id : sort === 'oldest' ? Date.parse(a.created_at) - Date.parse(b.created_at) : Date.parse(b.created_at) - Date.parse(a.created_at));
    }, [leads, query, status, active, day, attendance, sort]);

    const pages = Math.max(1, Math.ceil(filtered.length / size)),
        current = Math.min(page, pages),
        rows = filtered.slice((current - 1) * size, current * size),
        checked = leads.filter(l => l.checked_in).length;

    function reset() {
        setQuery('');
        setStatus('all');
        setActive('all');
        setDay('all');
        setAttendance('present');
        setSort('newest');
        setPage(1)
    }

    async function toggle(l) {
        if (busy !== null) return;
        setConfirm(null);
        setBusy(l.id);
        setError('');
        setNotice('');
        try {
            const r = await fetch(`https://silvamethod.in/manage/api/v1/goausers/manage-goa-users/${encodeURIComponent(l.id)}`, { method: 'PATCH' });
            const j = await r.json();
            if (!r.ok || j.success === false) throw Error(j.message || 'The check-in could not be updated.');
            setNotice(`${l.full_name} ${l.checked_in ? 'checked out' : 'checked in'} successfully.`);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Update failed')
        } finally {
            setBusy(null)
        }
    };


    async function deleteAttendee() {
        if (!deleteTarget || busy !== null) return;

        const attendee = deleteTarget;
        setBusy(attendee.id);
        setError('');
        setNotice('');

        try {
            const response = await fetch(
                `${API}/delete-goa-users/${encodeURIComponent(attendee.id)}`,
                {
                    method: 'DELETE'
                },
            );

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Could not delete attendee.');
            }

            setDeleteTarget(null);
            setNotice(`${attendee.full_name || 'Attendee'} deleted successfully.`);
            await load();
        } catch (cause) {
            setError(
                cause instanceof Error ? cause.message : 'Could not delete attendee.',
            );
            setDeleteTarget(null);
        } finally {
            setBusy(null);
        }
    }


    return <div className="min-h-screen bg-purple-50/40 text-slate-900">
        <main className="mx-auto max-w-7xl px-5 py-8 md:px-10 md:py-10">
            <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="mb-2 text-xs font-bold uppercase tracking-[.16em] text-purple-700">
                        Attendee directory
                    </div>
                    <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">Goa leads</h1>
                    <p className="mt-2 text-sm text-slate-500">
                        Manage attendees, check-ins and daily attendance.
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <span className="hidden text-xs text-slate-500 sm:block">
                        {updated ? `Updated ${updated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })} IST` : 'Waiting for data'}
                    </span>
                    <button onClick={() => void load()} disabled={loading || busy !== null} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium disabled:opacity-50">
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />Refresh
                    </button>
                    <button
                        type="button"
                        disabled={loading || busy !== null}
                        onClick={() => setShowAddModal(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-purple-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-purple-950 disabled:opacity-50"
                    >
                        <Plus size={16} />
                        Add attendee
                    </button>
                </div>
            </div>
            <ExcelTools leads={leads} filtered={filtered} disabled={loading || busy !== null} onSaved={load} />
            <section className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Lead summary">
                {
                    [{ label: 'Total leads', value: leads.length, icon: Users, caption: 'All registered attendees' },
                    { label: 'Checked in', value: checked, icon: UserCheck, caption: 'Currently checked in' },
                    { label: 'Not checked in', value: leads.length - checked, icon: Clock, caption: 'Awaiting check-in' },
                    { label: 'Active leads', value: leads.filter(l => l.is_active).length, icon: CalendarDays, caption: 'Active registrations' }
                    ].map((s, i) =>
                        <div key={s.label} className={`rounded-xl border p-4 md:p-5 ${i === 1 ? 'border-purple-200 bg-purple-50' : 'border-slate-200 bg-white'}`}>
                            <div className="flex items-center justify-between gap-2 text-sm font-medium text-slate-600">
                                {s.label}
                                <s.icon size={19} className={i === 1 ? 'text-purple-700' : 'text-slate-400'} />
                            </div>
                            <div className="mt-4 text-3xl font-semibold">
                                {loading && !updated ? '—' : s.value}
                            </div>
                            <div className="mt-2 hidden text-xs text-slate-500 sm:block">
                                {s.caption}
                            </div>
                        </div>
                    )}
            </section>
            {error && <div role="alert" className="mb-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <AlertCircle size={18} className="shrink-0" />
                {error}
                <button className="ml-auto underline" onClick={() => void load()}>
                    Retry
                </button>
            </div>
            }
            {notice && <div role="status" className="mb-5 flex gap-3 rounded-lg border border-purple-200 bg-purple-50 p-4 text-sm text-purple-800">
                <Check size={18} />
                {notice}
                <button aria-label="Dismiss notification" className="ml-auto" onClick={() => setNotice('')}>
                    <X size={16} />
                </button>
            </div>
            }
            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="border-b border-slate-200 p-5 md:p-6">
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold">
                                All attendees
                            </h2>
                            <span className="rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600">
                                {filtered.length} leads
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                            <SlidersHorizontal size={16} />Search & filters
                        </div>
                    </div>
                    <div className="relative mb-4">
                        <Search size={19} className="absolute left-3.5 top-3 text-slate-400" />
                        <input aria-label="Search attendees" placeholder="Search by name, email, phone or lead ID…" value={query} onChange={e => setQuery(e.target.value)} className={`${field} w-full pl-11 pr-10`} />
                        {query && <button aria-label="Clear search" className="absolute right-3 top-3 text-slate-500" onClick={() => setQuery('')}>
                            <X size={18} />
                        </button>
                        }
                    </div>
                    <div className="grid grid-cols-2 items-end gap-3 md:grid-cols-4 lg:flex lg:flex-wrap">
                        {[{ label: 'Check-in', value: status, set: setStatus, opts: [['all', 'All check-ins'], ['in', 'Checked in'], ['out', 'Not checked in']] },
                        { label: 'Registration', value: active, set: setActive, opts: [['all', 'All registrations'], ['active', 'Active'], ['inactive', 'Inactive']] },
                        { label: 'Attendance day', value: day, set: setDay, opts: [['all', 'All days'], ...Array.from({ length: 5 }, (_, i) => [`day${i + 1}`, `Day ${i + 1}`])] },
                        { label: 'Sort by', value: sort, set: setSort, opts: [['newest', 'Newest first'], ['oldest', 'Oldest first'], ['name', 'Name A–Z'], ['id', 'Lead ID']] }].map(f => <label key={f.label} className="flex min-w-0 flex-col gap-1.5 text-xs font-medium text-slate-500 lg:min-w-40">
                            <span>
                                {f.label}
                            </span>
                            <select className={field} value={f.value} onChange={e => f.set(e.target.value)}>{f.opts.map(([v, t]) =>
                                <option key={v} value={v}>
                                    {t}
                                </option>
                            )}
                            </select>
                        </label>
                        )}
                        {day !== 'all' &&
                            <label className="flex flex-col gap-1.5 text-xs text-slate-500">
                                Attendance
                                <select className={field} value={attendance} onChange={e => setAttendance(e.target.value)}>
                                    <option value="present">Attended</option>
                                    <option value="absent">Not attended</option>
                                </select>
                            </label>
                        }
                        <button onClick={reset} className="px-2 py-3 text-sm text-purple-700 lg:ml-auto">
                            Reset filters
                        </button>
                    </div>
                </div>
                {loading && !updated ? <div role="status" className="flex justify-center gap-3 py-20 text-slate-500">
                    <Loader2 className="animate-spin" />
                    Loading attendees…
                </div> : rows.length === 0 ? <div className="p-14 text-center">
                    <Users size={32} className="mx-auto mb-4 text-slate-300" />
                    <h3 className="font-semibold">
                        {error ? 'Attendees unavailable' : leads.length ? 'No matching attendees' : 'No attendees yet'}
                    </h3>
                    <p className="mt-2 text-sm text-slate-500">{error ? 'Refresh to try loading the directory again.' : leads.length ? 'Try a different search or reset your filters.' : 'New registrations will appear here after refresh.'}
                    </p>
                    {leads.length > 0 && <button onClick={reset} className="mt-5 text-sm font-medium text-purple-700">
                        Clear search and filters
                    </button>
                    }
                </div> : <>
                    <div className="hidden overflow-x-auto lg:block">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                                <tr>
                                    {['Attendee', 'Registration', 'Feedback Sent', 'Check-in', 'Action'].map(t =>
                                        <th key={t} className="px-6 py-4 font-medium">{t}</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">{rows.map(l =>
                                <tr key={l.id} className="hover:bg-slate-50/70">
                                    <td className="px-6 py-5">
                                        <div className="flex gap-3">
                                            <Avatar lead={l} />
                                            <div>
                                                <div className="font-semibold">
                                                    {l.full_name || 'Unnamed attendee'}
                                                    <span className="ml-1 text-xs font-normal text-slate-400">
                                                        #{l.id}
                                                    </span>
                                                </div>
                                                <div className="mt-1 text-slate-500">
                                                    {l.email || 'No email'}
                                                </div>
                                                <div className="mt-1 text-xs text-slate-500">
                                                    {l.phone || 'No phone'}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className={`rounded-md px-2 py-1 text-xs font-medium ${l.is_active ? 'bg-purple-50 text-purple-700' : 'bg-slate-100 text-slate-600'}`}>
                                            {l.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                        <div className="mt-3 text-xs text-slate-500">
                                            {date(l.created_at)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <Days lead={l} />
                                    </td>
                                    <td className="px-6 py-5">
                                        <Status lead={l} />
                                    </td>
                                    <td className="px-6 py-5">
                                        <Action lead={l} busy={busy} onClick={() => setConfirm(l)} onDelete={() => setDeleteTarget(l)} />
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                    <div className="divide-y divide-slate-100 lg:hidden">{rows.map(l =>
                        <article key={l.id} className="p-5">
                            <div className="flex items-start gap-3">
                                <Avatar lead={l} />
                                <div className="min-w-0 flex-1">
                                    <h3 className="font-semibold">
                                        {l.full_name || 'Unnamed attendee'}
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        Lead #{l.id} ·
                                        {l.is_active ? 'Active' : 'Inactive'}
                                    </p>
                                </div>
                            </div>
                            <div className="my-4 space-y-1 break-words text-sm text-slate-500">
                                <p>{l.email || 'No email'}</p>
                                <p>{l.phone || 'No phone'}</p>
                            </div><Days lead={l} />
                            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                                <Status lead={l} />
                                <Action lead={l} busy={busy} onClick={() => setConfirm(l)} onDelete={() => setDeleteTarget(l)} />
                            </div>
                        </article>
                    )}
                    </div>
                </>
                }
                <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 px-5 py-4 text-sm text-slate-500">
                    <div>Showing {filtered.length ? (current - 1) * size + 1 : 0}–{Math.min(current * size, filtered.length)} of {filtered.length}</div>
                    <div className="flex flex-wrap items-center gap-3">
                        <label className="flex items-center gap-2">
                            Rows
                            <select aria-label="Rows per page" className="rounded border border-slate-200 p-1.5" value={size} onChange={e => setSize(Number(e.target.value))}>
                                {[10, 25, 50].map(n =>
                                    <option key={n}>{n}</option>
                                )}
                            </select>
                        </label>
                        <span>{current} / {pages}</span>
                        <button aria-label="Previous page" disabled={current === 1} onClick={() => setPage(current - 1)} className="rounded border border-slate-200 p-2 disabled:opacity-30">
                            <ChevronLeft size={16} />
                        </button>
                        <button aria-label="Next page" disabled={current === pages} onClick={() => setPage(current + 1)} className="rounded border border-slate-200 p-2 disabled:opacity-30">
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </section>
            <div className="mt-4 flex flex-wrap justify-between gap-2 text-xs text-slate-500">
                <span>Daily attendance: ✓ attended · — not attended</span>
                <span>All times shown in India Standard Time (IST)</span>
            </div>
        </main>
        {confirm && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-5" onClick={() => setConfirm(null)}>
            <div role="dialog" aria-modal="true" aria-labelledby="confirm-title" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()} onKeyDown={e => { if (e.key === 'Escape') setConfirm(null) }}>
                <h2 id="confirm-title" className="text-xl font-semibold">{confirm.checked_in ? 'Check out attendee?' : 'Check in attendee?'}</h2>
                <p className="mt-3 text-slate-600">Update the check-in status for
                    <strong>{confirm.full_name}</strong>
                    (#{confirm.id})?
                </p>
                <div className="mt-6 flex justify-end gap-3">
                    <button autoFocus onClick={() => setConfirm(null)} className="rounded-lg border border-slate-200 px-4 py-2.5">
                        Cancel
                    </button>
                    <button onClick={() => void toggle(confirm)} className="rounded-lg bg-purple-700 px-4 py-2.5 font-medium text-white">
                        {confirm.checked_in ? 'Check out' : 'Check in'}
                    </button>
                </div>
            </div>
        </div>
        }

        {showAddModal && (
            <AddAttendeeModal
                onClose={() => setShowAddModal(false)}
                onCreated={async attendee => {
                    setShowAddModal(false);
                    setNotice(`${attendee.full_name} added successfully.`);
                    await load();
                }}
            />
        )}

        {deleteTarget && (
            <DeleteAttendeeModal
                attendee={deleteTarget}
                saving={busy !== null}
                onClose={() => {
                    if (busy === null) setDeleteTarget(null);
                }}
                onConfirm={deleteAttendee}
            />
        )}
    </div>
}
function Avatar({ lead }) {
    return <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f3e8ff] text-sm font-semibold text-[#6b21a8]">
        {(lead.full_name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase()}
    </div>
}
function Days({ lead }) {
    return <div className="flex gap-1.5">{[1, 2, 3, 4, 5].map(n =>
        <div key={n} className="text-center">
            <div className="mb-1 text-xs text-slate-500">
                D{n}
            </div>
            <span title={`Day ${n}: ${lead[`day${n}`] ? date(lead[`day${n}`]) : 'Not attended'}`} aria-label={`Day ${n}: ${lead[`day${n}`] ? 'attended' : 'not attended'}`} className={`flex h-7 w-7 items-center justify-center rounded-md ${lead[`day${n}`] ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-400'}`}>
                {lead[`day${n}`] ? <Check size={14} /> : '—'}
            </span>
        </div>
    )}
    </div>
}
function Status({ lead }) {
    return <div>
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${lead.checked_in ? 'bg-purple-50 text-purple-800' : 'bg-amber-50 text-amber-800'}`}>
            {lead.checked_in ? <Check size={12} /> : <Clock size={12} />}
            {lead.checked_in ? 'Checked in' : 'Not checked in'}
        </span>{lead.checked_in_at &&
            <div className="mt-2 text-xs text-slate-500">
                {date(lead.checked_in_at)}
            </div>
        }
    </div>
}

function Action({ lead, busy, onClick, onDelete }) {
    const updating = busy === lead.id;

    return (
        <div className="flex flex-wrap items-center gap-2">
            <button
                type="button"
                disabled={busy !== null}
                onClick={onClick}
                className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50 ${lead.checked_in
                    ? 'border border-slate-200 bg-white text-slate-600'
                    : 'bg-purple-700 text-white hover:bg-purple-800'
                    }`}
            >
                {updating && <Loader2 size={14} className="animate-spin" />}
                {updating ? 'Processing…' : lead.checked_in ? 'Check out' : 'Check in'}
            </button>

            <button
                type="button"
                disabled={busy !== null}
                onClick={onDelete}
                aria-label={`Delete ${lead.full_name || 'attendee'}`}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
                <Trash2 size={15} />
                Delete
            </button>
        </div>
    );
}

function ExcelTools({ leads, filtered, disabled, onSaved }) {
    const input = useRef(null);
    const [preview, setPreview] = useState(null);
    const [filename, setFilename] = useState('');
    const [working, setWorking] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [previewPage, setPreviewPage] = useState(1);
    const [scope, setScope] = useState('all');

    async function post(body) {
        const response = await fetch(`${API}/bulk-update-goa-users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.message || 'The import request failed.');
        return payload;
    }

    function download() {
        setError('');
        try {
            const rows = scope === 'all' ? leads : filtered;
            if (!rows.length) throw new Error('No attendees to export.');
            const worksheet = XLSX.utils.json_to_sheet(rows.map(lead => Object.fromEntries(
                EXCEL_COLUMNS.map(key => [key, lead[key] ?? '']),
            )), { header: EXCEL_COLUMNS });
            worksheet['!cols'] = EXCEL_COLUMNS.map(key => ({ wch: ['email', 'checked_in_at', 'created_at'].includes(key) || key.startsWith('day') ? 30 : 22 }));
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendees');
            XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([
                ['Import instructions'],
                ['Edit Attendees and keep each existing id unchanged. Maximum 500 rows per import.'],
                ['Blank cells preserve existing values. [CLEAR] explicitly clears a nullable field.'],
                ['Use TRUE/FALSE for is_active and checked_in.'],
                ['Keep timestamp columns as ISO text. Phone numbers must be text.'],
                ['created_at is read-only. Unknown IDs are rejected. Rows absent from the file are untouched.'],
                ['Only changed fields are updated. Review the server preview before applying.'],
            ]), 'Instructions');
            saveAs(new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }), `goa-attendees-${scope}-${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (cause) { setError(cause.message || 'Export failed.'); }
    }

    async function upload(event) {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setWorking(true);
        setError('');
        setMessage('');
        setResult(null);
        setPreview(null);
        try {
            if (!/\.xlsx?$/i.test(file.name)) throw new Error('Choose an .xlsx or .xls workbook.');
            if (file.size > 5 * 1024 * 1024) throw new Error('The workbook must be smaller than 5 MB.');
            const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: false, sheetRows: 502 });
            const sheet = workbook.Sheets[workbook.SheetNames.includes('Attendees') ? 'Attendees' : workbook.SheetNames[0]];
            if (!sheet) throw new Error('The workbook has no worksheet.');
            const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: '', blankrows: true });
            const headers = (matrix.shift() || []).map(value => String(value).trim());
            if (!headers.includes('id')) throw new Error('Missing id column. Use the attendee export as your template.');
            const names = headers.filter(Boolean);
            if (new Set(names).size !== names.length) throw new Error('Duplicate column headers are not allowed.');
            const unknown = names.filter(key => !EXCEL_COLUMNS.includes(key) && key !== 'updated_at');
            if (unknown.length) throw new Error(`Unsupported columns: ${unknown.join(', ')}.`);
            // Preserve spreadsheet row numbering by rejecting interior empty rows.
            while (matrix.length && matrix[matrix.length - 1].every(value => value === '')) matrix.pop();
            if (!matrix.length || matrix.length > 500) throw new Error('Upload between 1 and 500 attendee rows. Split larger sheets into batches.');
            const rows = matrix.map(cells => {
                if (cells.some((value, index) => value !== '' && !headers[index])) throw new Error('A populated column is missing its header.');
                return Object.fromEntries(headers.filter(Boolean).map(key => {
                    const index = headers.indexOf(key);
                    let value = cells[index] ?? '';
                    // Exported phone cells are strings. Do not silently lose leading zeros.
                    if (key === 'phone' && typeof value === 'number') throw new Error('Phone numbers must be formatted as Text in Excel.');
                    return [key, value];
                }));
            });
            const payload = await post({ rows, dryRun: true });
            if (!payload.success) throw new Error(payload.message);
            setFilename(file.name);
            setPreview(payload.data);
            setPreviewPage(1);
        } catch (cause) { setError(cause.message || 'Could not read workbook.'); }
        finally { setWorking(false); }
    }

    async function apply() {
        if (!preview || working || preview.summary.invalid || !pendingCount) return;
        setWorking(true);
        setError('');
        try {
            // Submit exactly the changed fields and baseline values returned by preview.
            const rows = preview.rows
                .filter(row => ['create', 'update'].includes(row.status))
                .map(({ id, patch, expected }) => ({ id, patch, expected }));
            const payload = await post({ rows, dryRun: false });
            setResult(payload.data);
            setPreviewPage(1);
            setPreview(null); // Prevent accidentally replaying a partially successful import.
            setMessage(payload.message);
            if (!payload.success) setError('Some rows failed. Review the results, then upload again for a fresh preview.');
            await onSaved();
        } catch (cause) {
            setError(`${cause.message || 'Import failed.'} Refresh and preview again before retrying.`);
            setPreview(null);
            await onSaved();
        } finally { setWorking(false); }
    }

    const pendingCount = (preview?.summary.create ?? 0) + (preview?.summary.update ?? 0);

    const displayRows = preview?.rows || result?.rows || [];
    const pageCount = Math.max(1, Math.ceil(displayRows.length / 10));
    const visibleRows = displayRows.slice((previewPage - 1) * 10, previewPage * 10);
    return <section className="mb-6 rounded-2xl border border-purple-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
                <h2 className="font-semibold text-purple-950">Excel workspace</h2>
                <p className="mt-1 max-w-2xl text-sm text-slate-500">Export, edit and preview updates. Existing IDs identify attendees; blank cells and omitted rows preserve your data.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <select aria-label="Export scope" value={scope} onChange={event => setScope(event.target.value)} className={field}>
                    <option value="all">All attendees</option><option value="filtered">Filtered attendees</option>
                </select>
                <button disabled={disabled || working} onClick={download} className="rounded-lg border border-purple-200 px-4 py-2.5 text-sm font-medium text-purple-800 disabled:opacity-50">Download Excel</button>
                <button disabled={disabled || working} onClick={() => input.current?.click()} className="inline-flex items-center gap-2 rounded-lg bg-purple-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-purple-950 disabled:opacity-50">
                    {working && <Loader2 size={16} className="animate-spin" />}{working ? 'Processing…' : 'Upload Excel'}
                </button>
                <input ref={input} type="file" accept=".xlsx,.xls" onChange={upload} className="hidden" aria-label="Upload attendee workbook" />
            </div>
        </div>
        {error && <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        {message && <p role="status" className="mt-4 rounded-lg bg-purple-50 p-3 text-sm text-purple-900">{message}</p>}
        {(preview || result) && <div className="mt-5 border-t border-purple-100 pt-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="font-semibold">{preview ? 'Import preview' : 'Import results'} <span className="ml-2 text-xs font-normal text-slate-500">{filename}</span></h3>
                    {preview && <p className="mt-1 text-sm text-slate-600">
                        {preview.summary.create} new · {preview.summary.update} updates ·{' '}
                        {preview.summary.unchanged} unchanged · {preview.summary.invalid} invalid.
                        No changes saved yet.
                    </p>}
                </div>
                <button disabled={working} onClick={() => { setPreview(null); setResult(null); }} className="rounded-lg p-2 text-slate-500" aria-label="Close import preview"><X size={18} /></button>
            </div>
            <div className="mt-4 max-h-96 overflow-auto rounded-lg border border-slate-200">
                <table className="w-full min-w-[700px] text-left text-sm">
                    <thead className="sticky top-0 bg-purple-50 text-purple-950"><tr>{['Sheet row', 'ID / attendee', 'Status', 'Changes / errors'].map(label => <th key={label} className="px-4 py-3 font-medium">{label}</th>)}</tr></thead>
                    <tbody className="divide-y divide-slate-100">{visibleRows.map((row, index) => <tr key={`${row.row}-${index}`}>
                        <td className="px-4 py-3">{row.row}</td>
                        <td className="px-4 py-3"><div>#{row.id || '—'}</div><div className="text-xs text-slate-500">{row.name}</div></td>
                        <td className={`px-4 py-3 font-medium ${['invalid', 'failed'].includes(row.status) ? 'text-red-700' : 'text-purple-700'}`}>{row.status}</td>
                        <td className="px-4 py-3">{row.error || (Object.entries(row.patch || {}).length ? Object.entries(row.patch).map(([key, value]) => <div key={key} className="mb-1 break-all"><span className="font-medium">{key}</span>: <span className="text-slate-400">{String(row.expected?.[key] ?? '(empty)')}</span> → {String(value ?? '(clear)')}</div>) : 'No changes')}</td>
                    </tr>)}</tbody>
                </table>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 text-sm text-slate-500">
                    <button aria-label="Previous preview page" disabled={working || previewPage <= 1} onClick={() => setPreviewPage(value => value - 1)} className="rounded border p-2 disabled:opacity-40"><ChevronLeft size={16} /></button>
                    {previewPage} / {pageCount}
                    <button aria-label="Next preview page" disabled={working || previewPage >= pageCount} onClick={() => setPreviewPage(value => value + 1)} className="rounded border p-2 disabled:opacity-40"><ChevronRight size={16} /></button>
                </div>
                {preview &&
                    <button
                        disabled={working || disabled || preview.summary.invalid > 0 || !pendingCount}
                        onClick={apply}
                        className="rounded-lg bg-purple-800 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
                    >
                        {working ? 'Saving…' : `Apply ${pendingCount} changes`}
                    </button>}
            </div>
            <p className="mt-3 text-xs text-slate-500">Blank cells are ignored. Use [CLEAR] for nullable fields. Invalid rows block saving. Runtime errors can cause partial success; the list refreshes after every save attempt.</p>
        </div>}
    </section>;
};


function AddAttendeeModal({ onClose, onCreated }) {
    const dialogRef = useRef(null);
    const submittingRef = useRef(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        full_name: '',
        email: '',
        phone: '',
        is_active: true,
        checked_in: false,
    });

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog.open) dialog.showModal();

        return () => {
            if (dialog.open) dialog.close();
        };
    }, []);

    function update(event) {
        const { name, value, type, checked } = event.target;
        setForm(current => ({
            ...current,
            [name]: type === 'checkbox' ? checked : value,
        }));
    }

    async function submit(event) {
        event.preventDefault();
        if (submittingRef.current) return;

        submittingRef.current = true;
        setSaving(true);
        setError('');

        let attendee;

        try {
            const response = await fetch(`${API}/create-goa-users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    full_name: form.full_name.trim(),
                    email: form.email.trim().toLowerCase(),
                    phone: form.phone.trim(),
                }),
            });

            const result = await response.json();

            if (!response.ok || !result.success || !result.data) {
                throw new Error(result.message || 'Could not add attendee.');
            }

            attendee = result.data;
        } catch (cause) {
            setError(
                cause instanceof Error ? cause.message : 'Could not add attendee.',
            );
        } finally {
            submittingRef.current = false;
            setSaving(false);
        }

        if (attendee) await onCreated(attendee);
    }

    return (
        <dialog
            ref={dialogRef}
            aria-labelledby="add-attendee-title"
            onCancel={event => {
                event.preventDefault();
                if (!submittingRef.current) onClose();
            }}
            className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-2xl bg-white p-0 text-slate-900 shadow-2xl backdrop:bg-slate-950/60"
        >
            <form onSubmit={submit}>
                <div className="flex items-start justify-between gap-4 border-b border-purple-100 px-6 py-5">
                    <div>
                        <h2
                            id="add-attendee-title"
                            className="text-xl font-semibold text-purple-950"
                        >
                            Add attendee
                        </h2>
                        <p className="mt-1 text-sm text-slate-500">
                            Create a new Goa program registration.
                        </p>
                    </div>

                    <button
                        type="button"
                        aria-label="Close modal"
                        disabled={saving}
                        onClick={onClose}
                        className="rounded-lg p-2 text-slate-400 hover:bg-purple-50 hover:text-purple-800 disabled:opacity-50"
                    >
                        <X size={20} />
                    </button>
                </div>

                <fieldset disabled={saving} className="space-y-4 px-6 py-5">
                    {[
                        {
                            name: 'full_name',
                            label: 'Full name',
                            type: 'text',
                            autoComplete: 'name',
                            required: true,
                            maxLength: 320,
                        },
                        {
                            name: 'email',
                            label: 'Email address',
                            type: 'email',
                            autoComplete: 'email',
                            required: true,
                            maxLength: 320,
                        },
                        {
                            name: 'phone',
                            label: 'Phone number',
                            type: 'tel',
                            autoComplete: 'tel',
                            required: false,
                            maxLength: 40,
                        },
                    ].map(input => (
                        <label
                            key={input.name}
                            className="block text-sm font-medium text-slate-700"
                        >
                            {input.label}
                            {input.required && <span className="text-purple-700"> *</span>}

                            <input
                                name={input.name}
                                type={input.type}
                                autoComplete={input.autoComplete}
                                autoFocus={input.name === 'full_name'}
                                required={input.required}
                                maxLength={input.maxLength}
                                value={form[input.name]}
                                onChange={update}
                                className={`${field} mt-1.5 w-full disabled:bg-slate-50`}
                            />
                        </label>
                    ))}

                    <div className="space-y-3 rounded-xl bg-purple-50 p-4">
                        {[
                            ['is_active', 'Active registration'],
                            ['checked_in', 'Check in immediately'],
                        ].map(([name, label]) => (
                            <label
                                key={name}
                                className="flex items-center gap-3 text-sm text-purple-950"
                            >
                                <input
                                    type="checkbox"
                                    name={name}
                                    checked={form[name]}
                                    onChange={update}
                                    className="h-4 w-4 accent-purple-800"
                                />
                                {label}
                            </label>
                        ))}
                    </div>
                </fieldset>

                {error && (
                    <div
                        role="alert"
                        className="mx-6 mb-5 rounded-lg bg-red-50 p-3 text-sm text-red-800"
                    >
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-3 border-t border-purple-100 px-6 py-4">
                    <button
                        type="button"
                        disabled={saving}
                        onClick={onClose}
                        className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium disabled:opacity-50"
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-lg bg-purple-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-purple-950 disabled:opacity-50"
                    >
                        {saving && <Loader2 size={16} className="animate-spin" />}
                        {saving ? 'Adding…' : 'Add attendee'}
                    </button>
                </div>
            </form>
        </dialog>
    );
}

function DeleteAttendeeModal({ attendee, saving, onClose, onConfirm }) {
    const dialogRef = useRef(null);
    const submittingRef = useRef(false);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog.open) dialog.showModal();

        return () => {
            if (dialog.open) dialog.close();
        };
    }, []);

    async function submit(event) {
        event.preventDefault();
        if (saving || submittingRef.current) return;

        submittingRef.current = true;

        try {
            await onConfirm();
        } finally {
            submittingRef.current = false;
        }
    }

    return (
        <dialog
            ref={dialogRef}
            aria-labelledby="delete-attendee-title"
            aria-describedby="delete-attendee-description"
            onCancel={event => {
                event.preventDefault();
                if (!saving && !submittingRef.current) onClose();
            }}
            className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-2xl bg-white p-6 text-slate-900 shadow-2xl backdrop:bg-slate-950/60"
        >
            <form onSubmit={submit}>
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 text-red-600">
                    <Trash2 size={23} />
                </div>

                <h2 id="delete-attendee-title" className="text-xl font-semibold">
                    Delete attendee?
                </h2>

                <p
                    id="delete-attendee-description"
                    className="mt-3 text-sm leading-6 text-slate-600"
                >
                    Permanently delete{' '}
                    <strong className="text-slate-900">
                        {attendee.full_name || 'this attendee'}
                    </strong>{' '}
                    (#{attendee.id}) and their attendance record? This cannot be undone.
                </p>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        type="button"
                        autoFocus
                        disabled={saving}
                        onClick={() => {
                            if (!submittingRef.current) onClose();
                        }}
                        className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium disabled:opacity-50"
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                    >
                        {saving && <Loader2 size={16} className="animate-spin" />}
                        {saving ? 'Deleting…' : 'Delete attendee'}
                    </button>
                </div>
            </form>
        </dialog>
    );
}
